import { useEffect, useState } from "react";
import logo from "@/assets/mckay-logo.png.asset.json";

/**
 * Minimal branded splash loader that plays once per session on the homepage.
 * Deep-navy overlay with the McKay logo, tagline, and three pulsing dots.
 * Fades out to reveal the hero. Respects reduced-motion + a11y toggle.
 */
export function HomeIntro() {
  const [mounted, setMounted] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [done, setDone] = useState(false);

  const DURATION = 1600;
  const FADE = 500;

  useEffect(() => {
    if (typeof window === "undefined") return;

    let timers: number[] = [];
    let prevOverflow = "";

    const start = (force = false) => {
      const reduced =
        window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
        document.documentElement.classList.contains("a11y-reduce-motion");
      const seen = sessionStorage.getItem("mckay-loader-seen") === "1";
      if (!force && (reduced || seen)) {
        setDone(true);
        return;
      }

      sessionStorage.setItem("mckay-loader-seen", "1");
      setLeaving(false);
      setDone(false);
      setMounted(true);

      prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      timers.push(
        window.setTimeout(() => setLeaving(true), DURATION),
        window.setTimeout(() => {
          setDone(true);
          document.body.style.overflow = prevOverflow;
        }, DURATION + FADE),
      );
    };

    start(false);

    const replay = () => {
      timers.forEach((t) => clearTimeout(t));
      timers = [];
      start(true);
    };
    window.addEventListener("mckay:play-intro", replay);

    return () => {
      window.removeEventListener("mckay:play-intro", replay);
      timers.forEach((t) => clearTimeout(t));
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  if (done || !mounted) return null;

  return (
    <div
      aria-hidden="true"
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-primary transition-opacity ease-out ${
        leaving ? "opacity-0" : "opacity-100"
      }`}
      style={{ transitionDuration: `${FADE}ms` }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.45) 100%)",
        }}
      />

      <div className="relative flex flex-col items-center animate-[fadeInUp_600ms_ease-out_both]">
        <img
          src={logo.url}
          alt=""
          className="mb-6 h-auto w-[120px]"
          draggable={false}
        />
        <p className="text-base font-medium tracking-wide text-white/90 sm:text-lg">
          Roofing Homes Since 1981…
        </p>
        <div className="mt-5 flex items-center gap-2">
          <span
            className="h-2 w-2 rounded-full bg-white/90"
            style={{ animation: "loaderDot 1.2s ease-in-out infinite", animationDelay: "0ms" }}
          />
          <span
            className="h-2 w-2 rounded-full bg-white/90"
            style={{ animation: "loaderDot 1.2s ease-in-out infinite", animationDelay: "200ms" }}
          />
          <span
            className="h-2 w-2 rounded-full bg-white/90"
            style={{ animation: "loaderDot 1.2s ease-in-out infinite", animationDelay: "400ms" }}
          />
        </div>
      </div>

      <p className="absolute bottom-6 left-0 right-0 text-center text-xs text-white/60">
        © {new Date().getFullYear()} McKay Roofing & Solar. All rights reserved.
      </p>

      <style>{`
        @keyframes loaderDot {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.85); }
          40% { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}